<script setup lang="ts">
import { ref, watch } from 'vue'
import { type CloudFile, useCloudPicker } from '../../composables/useCloudPicker'

const props = defineProps<{
  file: CloudFile | null
}>()

const { openPicker } = useCloudPicker()

const file = ref<CloudFile | null>(null)

const emit = defineEmits<{
  (e: 'update:file', file: CloudFile | null): void
}>()

watch(
  () => props.file,
  (v) => {
    file.value = v
  },
  { immediate: true }
)

async function selectFile(provider: 'google' | 'dropbox' | 'local') {
  const files = await openPicker(provider)
  if (files.length) {
    file.value = files[0]
    emit('update:file', file.value)
  }
}

function removeFile() {
  if (file.value?.provider === 'local' && file.value.url) {
    URL.revokeObjectURL(file.value.url)
  }
  file.value = null
  emit('update:file', file.value)
}

function formatSize(bytes?: number) {
  if (!bytes) return ''
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`
}
</script>

<template>
  <div class="upload-box">
    <!-- Empty state -->
    <div v-if="!file">
      <button type="button" class="select-btn" @click="selectFile('local')">📁 Attach</button>

      <button type="button" class="select-btn" @click="selectFile('dropbox')"> 📁 Dropbox </button>

      <button type="button" class="select-btn" @click="selectFile('google')">
        📁 Google Drive
      </button>
    </div>

    <!-- Selected file -->
    <div v-else class="file-row">
      <img v-if="file.iconUrl" :src="file.iconUrl" class="file-icon" />

      <div class="file-info">
        <div class="file-name">{{ file.name }}</div>
        <div class="file-meta">
          {{ file.mimeType }}
          <span v-if="file.size"> • {{ formatSize(file.size) }}</span>
        </div>
      </div>

      <button type="button" class="remove-btn" @click="removeFile">✕</button>
    </div>
  </div>
</template>

<style scoped>
.upload-box {
  border: 2px dashed #ccc;
  padding: 16px;
  border-radius: 8px;
  max-width: 420px;
}

.select-btn {
  width: 100%;
  padding: 12px;
  cursor: pointer;
  margin-bottom: 10px;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-icon {
  width: 40px;
  height: 40px;
}

.file-info {
  flex: 1;
}

.file-name {
  font-weight: 600;
}

.file-meta {
  font-size: 0.85rem;
  color: #666;
}

.remove-btn {
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
}
</style>
